import DateFnsUtils from "@date-io/date-fns";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { format, isValid, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import ruLocale from 'date-fns/locale/ru';
import React, { memo, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../core/consts/redesign/commonRedesign";
import { Calendar } from "../../../core/consts/redesign/icons";
import classes from "./FilterProducts.module.scss";

/* input and date picker with gray-blue bottom border */

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    datePicker: {
      "& .MuiInputBase-root": {
        paddingBottom: '6px',
        fontFamily: 'Gilroy',
        fontSize: '21px',
        fontStyle: 'normal',
        fontWeight: 500,
        color: COLORS.black,
        borderBottom: `2px solid ${COLORS.light_gray_bg}`,

        "&:hover": {
          borderBottom: `2px solid ${COLORS.brand_blue}`,
        },

      },
      '& .MuiInput-underline:before': {
        border: 'none',
      },
      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
        border: 'none',
      },
      '& .MuiIconButton-root': {
        '&:hover': {
          backgroundColor: 'transparent'
        },
      },
      "& .MuiInputBase-input": {
        color: 'inherit',
        "&::placeholder": {
          color: COLORS.black,
          opacity: 1,
        },
      },
      "& .MuiInputBase-input::placeholder": {
        color: COLORS.black,
      },
    },
    popover: {
      position: 'absolute',
      zIndex: 1200,
    },
  })
);

type PropsType = {
  onChange: (val: string) => void;
  label: string;
  value: string;
  placeholder?: string;
  id?: string;
  setIsFiltersChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

const DateSelect = ({
  label,
  placeholder = "ДД.MM.ГГГГ",
  id,
  value,
  onChange,
  setIsFiltersChanged
}: PropsType) => {
  const inlineClass = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<Date | null>(value ? new Date(value) : null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      const parsedDate = parse(value, 'dd-MM-yyyy', new Date());
      setInputValue(isValid(parsedDate) ? parsedDate : null);
    } else {
      setInputValue(null);
    }
  }, [value]);

  const handleDateChange = (newValue: Date | null) => {
    if (!newValue) {
      setInputValue(null);
      setErrorMessage(null);
      onChange('');
      if (setIsFiltersChanged) setIsFiltersChanged(true);
    } else if (isValid(newValue)) {
      const formattedDate = format(newValue, 'dd-MM-yyyy', { locale: ru });

      setInputValue(newValue);
      onChange(formattedDate);
      setErrorMessage(null);
      if (setIsFiltersChanged) setIsFiltersChanged(true);
    } else {
      setInputValue(newValue);
      setErrorMessage("Пожалуйста, введите корректную дату");
    }
    setIsOpen(false);
  };

  return (
    <div
      className={classes.dateContainer}
      id={id}
      ref={divRef}
    >
      <label className={classes.label}>{label}</label>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ruLocale}>
        <KeyboardDatePicker
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          value={inputValue}
          onChange={handleDateChange}
          format="dd.MM.yyyy"
          placeholder={placeholder}
          variant="inline"
          inputVariant="standard"
          className={inlineClass.datePicker}
          KeyboardButtonProps={{
            'aria-label': 'change date',
            onClick: () => setIsOpen((prev) => !prev),
          }}
          InputProps={{
            inputRef: inputRef,
          }}
          InputLabelProps={{ shrink: true }}
          keyboardIcon={
            <div onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)} >
              <Calendar color={isHovered ? COLORS.brand_blue : undefined} />
            </div>
          }
          helperText={errorMessage}
          error={!!errorMessage}
          PopoverProps={{
            className: inlineClass.popover,
            anchorEl: inputRef.current,

          }}
        />
      </MuiPickersUtilsProvider>
    </div>
  );
};

export default memo(DateSelect);
